import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BlahPipe } from '@headpower/blah-ng';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { InstructionSearchService } from './instruction-search.service';

@Component({
    selector: 'app-instruction-search',
    imports: [CommonModule, ReactiveFormsModule, BlahPipe, MatIconModule],
    templateUrl: './instruction-search.component.html',
    styleUrl: './instruction-search.component.scss'
})
export class InstructionSearchComponent implements OnInit, OnDestroy {
    private instructionSearchService = inject(InstructionSearchService);

    @Input() mobile: boolean = false;
    private destroy$: Subject<void> = new Subject();
    public isLoading: boolean = true;

    public isFocused: boolean = false;
    searchValue = new FormControl('');

    public highlightClass: string = 'iwi-search-highlight';
    public matches: HTMLElement[] = [];
    public currentIndex = -1;

    ngOnInit() {
        this.searchValue.valueChanges.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(value => {
            this.highlight(value.trim());
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    focus() {
        this.isFocused = true;
        this.instructionSearchService.triggerCollapseAll();
    }

    blur() {
        this.isFocused = false;
    }

    public closeKeyboard(event: any) {
        (event.target as HTMLInputElement).blur();
    }

    private getContainer(): Element | null {
        return document.querySelector('hpo-content-layout');
    }

    public clearHighlights(): void {
        const container = this.getContainer();
        if (!container) return;

        container.querySelectorAll(`.${this.highlightClass}, .${this.highlightClass}-selected`).forEach(el => {
            const parent = el.parentNode!;
            parent.replaceChild(document.createTextNode(el.textContent || ''), el);
            parent.normalize();
        });

        this.matches = [];
        this.currentIndex = -1;
    }

    public highlight(keyword: string): void {
        this.clearHighlights();
        if (!keyword) return;

        const container = this.getContainer();
        if (!container) return;

        const textNodes: Text[] = [];

        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
        while (walker.nextNode()) {
            textNodes.push(walker.currentNode as Text);
        }

        const regex = new RegExp(this.escapeRegExp(keyword), 'gi');

        textNodes.forEach(node => {
            const text = node.nodeValue || '';
            const matches = [...text.matchAll(regex)];
            if (matches.length === 0) return;

            const fragment = document.createDocumentFragment();
            let lastIndex = 0;

            matches.forEach(match => {
                const index = match.index!;
                const matchedText = match[0];

                if (index > lastIndex) {
                    fragment.appendChild(document.createTextNode(text.slice(lastIndex, index)));
                }

                const span = document.createElement('span');
                span.className = this.highlightClass;
                span.textContent = matchedText;
                fragment.appendChild(span);

                this.matches.push(span);

                lastIndex = index + matchedText.length;
            });

            if (lastIndex < text.length) {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
            }

            const parent = node.parentNode!;
            parent.replaceChild(fragment, node);
        });

        this.jumpTo(0);
    }

    private escapeRegExp(text: string): string {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    public jumpTo(index: number): void {
        if (this.matches.length === 0) return;

        this.currentIndex = ((index % this.matches.length) + this.matches.length) % this.matches.length;

        this.matches.forEach((el, i) => {
            el.className = i === this.currentIndex ? `${this.highlightClass}-selected` : `${this.highlightClass}`;
        });

        this.matches[this.currentIndex].scrollIntoView({ behavior: 'auto', block: 'center' });
    }

    public next() {
        this.jumpTo(this.currentIndex + 1);
    }

    public previous() {
        this.jumpTo(this.currentIndex - 1);
    }

    public resetSearch() {
        this.searchValue.setValue('');
    }
}
